'use client';

import { formatDate } from 'dates';
import { useRouter } from 'next/navigation';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, ContractCard as UiContractCard, Typography } from 'ui';
import { useResolution } from 'ui/hooks';

import getContractPath from '~/contract/helpers/getContractPath';
import { getStatusTranslation } from '~/contract/utils';
import { Contract } from '~/data/types';
import { LinkContainer } from '~/ui';

import { ContractDownloadIconButton } from './ContractDownloadIconButton';
import { ContractStatus } from './ContractStatus';

export const ContractCard = memo((contract: Contract) => {
  const { t } = useTranslation('contracts');
  const { startDate, createdDate, status, contractNumber, contractType, customerName, meteringPointsCount } = contract;

  const { push } = useRouter();

  const statusText = getStatusTranslation({ status, t });

  const statusColor = status === 'Active' || status === 'Signed' ? 'green' : 'orange.main';
  const showSignButton = status === 'Generated Contract' || status === 'Draft';

  const { isTablet } = useResolution();

  return (
    <LinkContainer href={getContractPath(contract)} key={contractNumber}>
      <UiContractCard
        subtitle={t('contractNumber', {
          contractNumber,
        })}
        title={contractType}
        data-testid="contract-card"
        downloadIcon={<ContractDownloadIconButton contractNumber={contractNumber} />}
        details={[
          <Box mb={1} key="contract-metering-points-count">
            <Typography variant="body2" color="textColor.light">
              {t('meteringPointsCount', {
                meteringPointsCount,
              })}
            </Typography>
          </Box>,
          <Box key="contract-status" mb={1} display="flex" alignItems="center" justifyContent="space-between">
            <Typography component="span" color={statusColor} variant="body2">
              {statusText}
            </Typography>
            {showSignButton && (
              <Button
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  push(`/contracts/${contractNumber}/sign-contract`);
                }}
                size={isTablet ? 'sm' : 'xs'}
              >
                {t('signContract')}
              </Button>
            )}
          </Box>,
          <Typography
            component="span"
            display="inline"
            variant="body2"
            color="textColor.light"
            key="contract-create-date"
          >
            {t('date', {
              date: formatDate({
                date: createdDate,
              }),
            })}
          </Typography>,
          <ContractStatus
            key="contract-active-from-date"
            status={status}
            startDate={startDate}
            createdDate={createdDate}
          />,
        ]}
        customerName={customerName}
      />
    </LinkContainer>
  );
});
